import { useEffect, useId, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { VisorDeCfdi } from '../componentes/VisorDeCfdi'
import { useTrampaDeFoco } from '../ganchos/useTrampaDeFoco'
import {
  AlertaDeCancelacion,
  api,
  CfdiDelRepositorio,
  Cliente,
  EmisorEnRiesgo,
  ErrorDeApi,
  EstadoSolicitudDescarga,
  ImportacionDeCfdi as AvanceDeImportacion,
  PaginaDeCfdi,
  ResumenDeReproceso,
  SolicitudDeDescarga,
  TipoComprobante,
  TipoDescargaCfdi,
} from '../servicios/api'
import { telon } from '../ganchos/telon'
import { useAvisos } from '../componentes/Toast'

/**
 * Intervalos de sondeo (RNF-04: proceso asíncrono con avance visible). Exportados para que las
 * pruebas los acorten; en producción no se tocan.
 */
export const configuracionDeSondeo = {
  /** Avance de una importación en curso. */
  importacionMs: 1000,
  /** Historial de descargas masivas mientras haya una solicitud viva (P-95: el SAT responde en la cola). */
  solicitudesMs: 15000,
}

const esperar = (ms: number) => new Promise<void>((resolver) => setTimeout(resolver, ms))

const ESTADOS_FALLIDOS: EstadoSolicitudDescarga[] = ['Error', 'Rechazada', 'Vencida']

const DESCRIPCION_DE_FALLO: Record<string, string> = {
  Error: 'falló al procesarse',
  Rechazada: 'el SAT la rechazó',
  Vencida: 'el SAT la dejó vencer antes de recogerla',
}

const LETRA: Record<TipoComprobante, string> = {
  Ingreso: 'I', Egreso: 'E', Traslado: 'T', Nomina: 'N', Pago: 'P',
}

const dinero = (n: number) =>
  n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const uuidCorto = (uuid: string) => `${uuid.slice(0, 8)}…${uuid.slice(-4)}`.toUpperCase()

/** El backend pagina de 50 en 50 (FiltroDeCfdi.Tamano); el pie necesita el mismo número. */
const TAMANO_DE_PAGINA = 50

/**
 * En qué quedó el comprobante (RF-601). «Retenido» va antes que «Pendiente» porque un CFDI de un
 * emisor del listado 69-B no se contabiliza sin revisarlo primero: decir «pendiente» invitaría a
 * hacer justo lo que hay que detener.
 */
function conciliacionDe(c: CfdiDelRepositorio, enRiesgo: boolean): { texto: string; grado: string } {
  if (c.estadoContabilizacion === 'Contabilizado')
    return { texto: c.polizaFolioVisible ? `En póliza ${c.polizaFolioVisible}` : 'Contabilizado', grado: 'cerrada' }
  if (c.estadoContabilizacion === 'Descartado') return { texto: 'Descartado', grado: 'neutra' }
  if (enRiesgo) return { texto: 'Retenido', grado: 'urgente' }
  return { texto: 'Pendiente', grado: 'suave' }
}

/** Repositorio de CFDI del cliente (RF-503, RF-504, RF-506 · cédula D). */
export function RepositorioCfdi() {
  const { id: clienteId } = useParams<{ id: string }>()
  const [cliente, setCliente] = useState<Cliente>()
  const [pagina, setPagina] = useState<PaginaDeCfdi>()
  const [tipo, setTipo] = useState<TipoComprobante | undefined>()
  const [busqueda, setBusqueda] = useState('')
  const [desde, setDesde] = useState('')
  const [hasta, setHasta] = useState('')
  const [numeroDePagina, setNumeroDePagina] = useState(1)
  const [error, setError] = useState('')
  const avisar = useAvisos()
  const [importando, setImportando] = useState(false)
  const [abierto, setAbierto] = useState<CfdiDelRepositorio | null>(null)
  const [descargando, setDescargando] = useState(false)
  const [solicitudes, setSolicitudes] = useState<SolicitudDeDescarga[]>([])
  const [alertas, setAlertas] = useState<AlertaDeCancelacion[]>([])
  const [emisoresEnRiesgo, setEmisoresEnRiesgo] = useState<EmisorEnRiesgo[]>([])
  const [verificando, setVerificando] = useState(false)
  const [seleccionados, setSeleccionados] = useState<Set<string>>(new Set())
  const [descargandoSeleccion, setDescargandoSeleccion] = useState(false)
  /**
   * RF-1201 · Tres estados a propósito, no dos: `undefined` es «todavía no se consulta»,
   * `'desconocido'` es «se consultó y falló» y un número es el dato real. Colapsar los dos
   * primeros en 0 pintaría «no hay pendientes» cuando lo cierto es que no se sabe.
   */
  const [complementosPendientes, setComplementosPendientes] = useState<number | 'desconocido'>()
  const [reprocesando, setReprocesando] = useState(false)
  const [ultimoReproceso, setUltimoReproceso] = useState<ResumenDeReproceso>()

  async function recargar() {
    if (!clienteId) return
    try {
      const [datosCliente, datos] = await Promise.all([
        api.obtenerCliente(clienteId),
        api.listarCfdi(clienteId, {
          tipo,
          busqueda: busqueda || undefined,
          desde: desde || undefined,
          hasta: hasta || undefined,
          pagina: numeroDePagina,
        }),
      ])
      setCliente(datosCliente)
      setPagina(datos)
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el repositorio.')
    }
  }

  async function recargarSolicitudes() {
    if (!clienteId) return
    try {
      setSolicitudes(await api.listarDescargasMasivas(clienteId))
    } catch {
      /* no bloquea el repositorio si esto falla */
    }
  }

  async function recargarAlertasYRiesgo() {
    if (!clienteId) return
    try {
      const [a, e] = await Promise.all([api.alertasDeCancelacion(clienteId), api.emisoresEnRiesgo(clienteId)])
      setAlertas(a)
      setEmisoresEnRiesgo(e)
    } catch {
      /* no bloquea el repositorio si esto falla */
    }
  }

  /**
   * RF-1201 · Cuántos comprobantes entraron antes de que el lector supiera mirar el complemento
   * de recepción de pagos. No depende de los filtros de la tabla: es del cliente entero.
   */
  async function recargarComplementosPendientes() {
    if (!clienteId) return
    try {
      const { pendientes } = await api.complementosDePagoPendientes(clienteId)
      setComplementosPendientes(pendientes)
    } catch {
      // No bloquea el repositorio, pero tampoco se calla: queda en «desconocido», que no es cero.
      setComplementosPendientes('desconocido')
    }
  }

  useEffect(() => {
    void recargar()
    void recargarSolicitudes()
    void recargarAlertasYRiesgo()
    setSeleccionados(new Set())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId, tipo, desde, hasta, numeroDePagina])

  // Aparte del efecto de arriba porque el conteo es por cliente, no por filtro: colgarlo de tipo,
  // fechas y página sería una consulta de más por cada clic en la tabla.
  useEffect(() => {
    setComplementosPendientes(undefined)
    setUltimoReproceso(undefined)
    void recargarComplementosPendientes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId])

  // Cambiar un filtro sin volver a la primera página deja la tabla vacía cuando el resultado
  // nuevo tiene menos páginas que la que estaba abierta.
  useEffect(() => {
    setNumeroDePagina(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, desde, hasta, busqueda])

  function alternarSeleccion(id: string) {
    setSeleccionados((actual) => {
      const nuevo = new Set(actual)
      if (nuevo.has(id)) nuevo.delete(id)
      else nuevo.add(id)
      return nuevo
    })
  }

  /** Descarga masiva (selección múltiple + ZIP): cierra el hueco de RF-506 sobre backend ya construido. */
  async function descargarSeleccionados() {
    if (!clienteId || seleccionados.size === 0) return
    setDescargandoSeleccion(true)
    setError('')
    try {
      await api.descargarCfdiSeleccionados(clienteId, [...seleccionados])
      setSeleccionados(new Set())
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo descargar la selección.')
    } finally {
      setDescargandoSeleccion(false)
    }
  }

  async function verificarEstatus() {
    if (!clienteId) return
    setVerificando(true)
    try {
      const r = await api.verificarEstatusDeCfdi(clienteId)
      avisar(
        `Estatus verificado: ${r.verificados} CFDI revisados, ${r.cancelados} cancelaciones nuevas` +
          (r.sinResultado ? `, ${r.sinResultado} sin respuesta del SAT.` : '.'),
      )
      await recargar()
      await recargarAlertasYRiesgo()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo verificar el estatus.')
    } finally {
      setVerificando(false)
    }
  }

  /**
   * RF-1201 · Relee el XML custodiado de los comprobantes pendientes. No le pide nada al
   * contribuyente: el XML ya está resguardado y verificado contra su huella (RN-04).
   */
  async function reprocesarComplementos() {
    if (!clienteId) return
    setReprocesando(true)
    setError('')
    try {
      const r = await api.reprocesarComplementosDePago(clienteId)
      setUltimoReproceso(r)
      avisar(
        `Complementos de pago: ${r.revisados} comprobante(s) releídos, ${r.conComplemento} traían `
          + `complemento y se indexaron ${r.parcialidadesNuevas} parcialidad(es)`
          + (r.tiposDeCambioCompletados
            ? `, ${r.tiposDeCambioCompletados} con tipo de cambio completado`
            : '')
          + (r.conError ? `. ${r.conError} no se pudieron releer.` : '.'),
      )
      await recargarComplementosPendientes()
      await recargar()
    } catch (e) {
      setError(
        e instanceof ErrorDeApi ? e.message : 'No se pudieron releer los complementos de pago.',
      )
    } finally {
      setReprocesando(false)
    }
  }

  async function atenderAlerta(a: AlertaDeCancelacion) {
    if (!clienteId) return
    try {
      await api.atenderAlertaDeCancelacion(clienteId, a.cfdiId)
      setAlertas((actual) => actual.filter((x) => x.cfdiId !== a.cfdiId))
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo atender la alerta.')
    }
  }

  const solicitudesActivas = solicitudes.filter(
    (s) => s.estado === 'Pendiente' || s.estado === 'Aceptada' || s.estado === 'EnProceso',
  )
  const solicitudActiva = solicitudesActivas[0]
  const ultimaTerminada = solicitudes.find((s) => s.estado === 'Terminada')

  // Una solicitud fallida no se veía en NINGUNA parte: el sondeo solo mira las vivas y la
  // línea de abajo solo la última Terminada. El contador se quedaba leyendo "última descarga
  // terminada" de hace meses mientras la que acababa de lanzar había fallado, y el motivo
  // —que el mensaje trae literal, p. ej. que la e.firma ya no está en el resguardo— vivía
  // nada más en la bitácora. El servicio devuelve el historial de la más nueva a la más
  // vieja, así que basta con mirar la primera: si esa falló, es el último intento.
  const ultimaFallida = ESTADOS_FALLIDOS.includes(solicitudes[0]?.estado) ? solicitudes[0] : undefined

  // Mientras haya una solicitud viva, el historial se refresca solo: la presentación al SAT y
  // la verificación corren en la cola, así que el estado cambia sin que el usuario haga nada.
  useEffect(() => {
    if (!solicitudActiva) return
    const temporizador = setInterval(() => void recargarSolicitudes(), configuracionDeSondeo.solicitudesMs)
    return () => clearInterval(temporizador)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudActiva?.id, clienteId])

  // Cuando la solicitud viva desaparece es porque terminó, y con ella se apaga el sondeo de
  // arriba. Para entonces ya hay CFDI nuevos en el repositorio, pero la tabla sigue mostrando
  // lo que había al entrar: el sondeo solo refresca el historial de solicitudes. Sin esto el
  // contador ve "descarga terminada" sobre un repositorio vacío y nada le dice que basta con
  // recargar la página. La ruta manual (verificarSolicitud) ya recargaba; esta no.
  const idDeSolicitudActiva = solicitudActiva?.id
  const habiaSolicitudActiva = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (habiaSolicitudActiva.current && !idDeSolicitudActiva) void recargar()
    habiaSolicitudActiva.current = idDeSolicitudActiva
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idDeSolicitudActiva])

  const descripcionDeEstado: Record<string, string> = {
    Pendiente: 'registrada, en espera de presentarse al SAT',
    Aceptada: 'aceptada por el SAT, aún sin generar',
    EnProceso: 'el SAT la está generando',
  }

  async function verificarSolicitud(s: SolicitudDeDescarga, forzar: boolean) {
    if (!clienteId) return
    try {
      await api.verificarDescargaMasiva(clienteId, s.id, forzar)
      await recargarSolicitudes()
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo verificar la solicitud.')
    }
  }

  async function verCotejo(s: SolicitudDeDescarga) {
    if (!clienteId) return
    try {
      const c = await api.cotejoDeCompletitud(clienteId, s.id)
      avisar(
        c.completo
          ? `Cotejo de completitud: ${c.encontradosEnRepositorio} de ${c.esperadosPorSat} CFDI del SAT están en el repositorio. Completo.`
          : `Cotejo de completitud: solo ${c.encontradosEnRepositorio} de ${c.esperadosPorSat} CFDI del SAT están en el repositorio. `
            + 'El motivo de cada archivo que no entró queda en Bitácora, filtrando por la entidad SolicitudDeDescarga.',
      )
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo hacer el cotejo de completitud.')
    }
  }

  if (error && !pagina) return <p className="error" role="alert">{error}</p>
  if (!clienteId || !cliente || !pagina) return <p>Cargando…</p>

  const totalRepositorio = Object.values(pagina.conteosPorTipo).reduce((s, n) => s + n, 0)
  const rfcsEnRiesgo = new Set(emisoresEnRiesgo.map((e) => e.rfcEmisor))

  return (
    <>
      <p className="sutil"><Link to={`/clientes/${clienteId}`}>← {cliente.razonSocial}</Link></p>
      <header className="encabezado">
        <h1>CFDI</h1>
        <span className="encabezado-sub mono">{cliente.rfc} · {cliente.razonSocial}</span>
        <span className="encabezado-acciones">
          <button disabled={verificando} onClick={() => void verificarEstatus()}>
            {verificando ? 'Verificando…' : 'Verificar estatus'}
          </button>
          <button data-recorrido="cfdi-importar" onClick={() => setImportando(true)}>Importar XML/ZIP</button>
          {seleccionados.size > 0 && (
            <button disabled={descargandoSeleccion} onClick={() => void descargarSeleccionados()}>
              {descargandoSeleccion ? 'Descargando…' : `Descargar seleccionados (${seleccionados.size})`}
            </button>
          )}
          {/* Traer los CFDI del SAT es la acción principal de esta pantalla; importar a mano es
              la excepción para lo que la descarga no alcanza. */}
          <button className="primario" data-recorrido="cfdi-descargar" onClick={() => setDescargando(true)}>Descargar por rango</button>
        </span>
      </header>

      <p className="menuda" style={{ margin: '0 0 var(--space-4)' }}>
        {totalRepositorio} comprobantes en el repositorio · deduplicados por UUID (RN-04) · el XML
        original se conserva íntegro al menos 5 años (RN-09).
      </p>

      {error && <p className="error" role="alert">{error}</p>}

      {solicitudActiva && (
        <p className="aviso" role="status">
          <strong>
            Descarga {solicitudActiva.tipo.toLowerCase()} {solicitudActiva.fechaInicial} a {solicitudActiva.fechaFinal}
            {solicitudesActivas.length > 1 ? ` (+${solicitudesActivas.length - 1} tramo(s) más del mismo lote)` : ''}:{' '}
            {descripcionDeEstado[solicitudActiva.estado]}.
          </strong>{' '}
          Se presenta y verifica sola en segundo plano con espera exponencial (RF-502). {solicitudActiva.mensaje}
          {' — '}
          <button className="enlace" onClick={() => void verificarSolicitud(solicitudActiva, true)}>
            {solicitudActiva.estado === 'Pendiente' ? 'Presentar al SAT ahora' : 'Forzar verificación ahora'}
          </button>
        </p>
      )}

      {ultimaFallida && (
        <div className="aviso-critico" role="alert">
          <span>
            <strong>
              La última descarga masiva ({ultimaFallida.fechaInicial} a {ultimaFallida.fechaFinal}){' '}
              no se completó: {DESCRIPCION_DE_FALLO[ultimaFallida.estado] ?? 'no terminó'}.
            </strong>{' '}
            {ultimaFallida.mensaje}
            {/* El dato ya venía en la respuesta y no se pintaba: sin él, «falló» no dice si el
                sistema ya lo intentó seis veces o si va a volver a intentarlo solo. */}
            {ultimaFallida.intentos > 0 && ` Se intentó ${ultimaFallida.intentos} vez(ces).`}
            {' '}El reintento nocturno no recoge una solicitud que ya terminó en error: para
            volver a pedir ese rango hay que lanzarlo de nuevo.
          </span>
          <button onClick={() => setDescargando(true)}>Volver a pedir el rango</button>
        </div>
      )}

      {/* RF-1201 · Mientras queden comprobantes sin releer, el IVA en base de flujo de este
          cliente sale incompleto, y hay que decirlo en la pantalla donde viven los CFDI: un cero
          con pendientes detrás no es un cero legítimo, es un dato que falta. */}
      {typeof complementosPendientes === 'number' && complementosPendientes > 0 && (
        <div className="aviso-critico" role="alert">
          <span>
            <strong>
              {complementosPendientes} comprobante(s) de este cliente entraron antes de que el
              sistema supiera leer el complemento de recepción de pagos.
            </strong>{' '}
            Hasta que se relean, lo que se haya cobrado en parcialidades no cuenta para el IVA en
            base de flujo y el cálculo del periodo queda corto. Reléelos desde el XML que ya está
            custodiado: no hay que pedirle nada al contribuyente ni volver a descargar del SAT.
          </span>
          <button disabled={reprocesando} onClick={() => void reprocesarComplementos()}>
            {reprocesando ? 'Releyendo…' : 'Releer complementos de pago'}
          </button>
        </div>
      )}

      {complementosPendientes === 'desconocido' && (
        <p className="sutil" role="status">
          No se pudo consultar cuántos comprobantes están pendientes de releer su complemento de
          pago. Eso no significa que no haya ninguno: significa que no se sabe. Vuelve a cargar la
          pantalla antes de fiarte de un cálculo de IVA en base de flujo de este cliente.
        </p>
      )}

      {/* Un comprobante que no se pudo releer sigue contando como pendiente, así que el motivo no
          se puede quedar solo en el aviso efímero: se queda a la vista hasta cambiar de cliente. */}
      {ultimoReproceso && ultimoReproceso.conError > 0 && (
        <div className="aviso-critico" role="alert">
          <span>
            <strong>
              {ultimoReproceso.conError} comprobante(s) no se pudieron releer y siguen pendientes.
            </strong>{' '}
            Vuelve a intentarlo; si el motivo se repite, es del XML custodiado y no del reproceso.
            <ul className="menuda" style={{ margin: 'var(--space-2) 0 0' }}>
              {ultimoReproceso.errores.map((motivo) => (
                <li key={motivo}>{motivo}</li>
              ))}
            </ul>
          </span>
        </div>
      )}

      {!solicitudActiva && ultimaTerminada && (
        <p className="sutil">
          Última descarga masiva terminada: {ultimaTerminada.fechaInicial} a {ultimaTerminada.fechaFinal} ·{' '}
          {ultimaTerminada.numeroDeCfdis} CFDI según el SAT en {ultimaTerminada.paquetesDescargados} paquete(s) ·{' '}
          <button className="enlace" onClick={() => void verCotejo(ultimaTerminada)}>Ver cotejo de completitud</button>
        </p>
      )}

      {alertas.length > 0 && (
        <div className="aviso-critico" role="alert" style={{ display: 'block' }}>
          <strong>{alertas.length} CFDI cancelado(s) por el SAT desde la última revisión:</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {alertas.map((a) => (
              <li key={a.cfdiId}>
                {a.nombreEmisor || a.rfcEmisor} · {uuidCorto(a.uuid)} · {dinero(a.total)}{' '}
                <button className="enlace" onClick={() => void atenderAlerta(a)}>Marcar como atendida</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {emisoresEnRiesgo.length > 0 && (
        <div className="aviso-critico" role="alert" style={{ display: 'block' }}>
          <strong>{emisoresEnRiesgo.length} emisor(es) recibido(s) aparecen en el listado 69-B del SAT:</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {emisoresEnRiesgo.map((e) => (
              <li key={e.rfcEmisor}>
                {e.nombreEmisor || e.rfcEmisor} ({e.situacion}) · {e.numeroDeCfdis} CFDI · {dinero(e.montoTotal)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="encabezado" data-recorrido="cfdi-repositorio" style={{ gap: 12, margin: '12px 0' }}>
        <form onSubmit={(e) => { e.preventDefault(); void recargar() }}>
          <input className="buscador compacta" style={{ margin: 0, width: 280 }} value={busqueda}
            aria-label="Buscar en el repositorio"
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="UUID, RFC, serie/folio, emisor, uso, moneda, total…" />
        </form>

        <div className="seg" role="radiogroup" aria-label="Tipo de comprobante">
          {([undefined, 'Ingreso', 'Egreso', 'Pago', 'Nomina', 'Traslado'] as (TipoComprobante | undefined)[]).map((t) => (
            <button key={t ?? 'todos'} type="button" className="seg-opcion"
              role="radio" aria-checked={t === tipo} onClick={() => setTipo(t)}>
              {t ? `${LETRA[t]} · ${pagina.conteosPorTipo[t] ?? 0}` : 'Todos'}
            </button>
          ))}
        </div>

        {/* El rango acota la tabla; no se confunde con «Descargar por rango», que pide CFDI al SAT. */}
        <label className="menuda" style={{ margin: 0 }}>
          Del
          <input type="date" className="compacta" aria-label="Emitidos desde" value={desde}
            onChange={(e) => setDesde(e.target.value)} style={{ width: 'auto', margin: '0 0 0 6px' }} />
        </label>
        <label className="menuda" style={{ margin: 0 }}>
          al
          <input type="date" className="compacta" aria-label="Emitidos hasta" value={hasta}
            onChange={(e) => setHasta(e.target.value)} style={{ width: 'auto', margin: '0 0 0 6px' }} />
        </label>
        {(desde || hasta) && (
          <button className="enlace" onClick={() => { setDesde(''); setHasta('') }}>Quitar el rango</button>
        )}
      </div>

      {pagina.items.length === 0 ? (
        <p className="vacio">Sin comprobantes con este filtro. Importa XML o ZIP para empezar.</p>
      ) : (
        <div className="tabla-envoltura">
        <table className="tabla">
          <thead>
            <tr>
              <th style={{ width: 24 }}></th>
              <th>Emisor / receptor</th><th>UUID</th><th>Tipo</th><th>Uso</th><th>Fecha</th>
              <th className="num">Total</th><th>Estatus SAT</th><th>Conciliación</th><th />
            </tr>
          </thead>
          <tbody>
            {pagina.items.map((c) => {
              const enRiesgo = !c.esEmitido && rfcsEnRiesgo.has(c.rfcEmisor)
              const conciliacion = conciliacionDe(c, enRiesgo)
              return (
              // La fila ya no es role="button": ahora lleva enlaces dentro y anidar controles
              // deja al teclado y al lector de pantalla con dos cosas donde hay una. El clic en
              // la fila sigue abriendo el visor; el recorrido por teclado va por el UUID.
              <tr key={c.id} onClick={() => setAbierto(c)}
                className={enRiesgo ? 'resaltada enlazable' : 'enlazable'}>
                <td onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" aria-label={`Seleccionar ${c.uuid}`}
                    checked={seleccionados.has(c.id)} onChange={() => alternarSeleccion(c.id)} />
                </td>
                <td>
                  <div style={{ fontWeight: 600 }}>
                    {c.esEmitido ? c.nombreReceptor || c.rfcReceptor : c.nombreEmisor || c.rfcEmisor}
                  </div>
                  <div className="sutil" style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {/* En un emitido la línea de arriba ya es el receptor: repetirlo aquí no dice nada. */}
                    {c.esEmitido ? 'emitido' : `→ ${cliente.razonSocial}`}
                  </div>
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <button className="enlace mono" title={c.uuid}
                    aria-label={`Ver el detalle del CFDI ${c.uuid}`}
                    onClick={() => setAbierto(c)}>{uuidCorto(c.uuid)}</button>
                </td>
                <td><span className="insignia neutra" style={{ minWidth: 22, justifyContent: 'center' }}>{LETRA[c.tipo]}</span></td>
                <td className="mono sutil">{c.usoCfdi ?? '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {new Date(c.fechaEmision).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}
                </td>
                <td className="num">{dinero(c.total)} {c.moneda !== 'MXN' ? c.moneda : ''}</td>
                <td>
                  {enRiesgo
                    ? <span className="insignia vencido" title="El emisor aparece en el listado 69-B del SAT">69-B presunto</span>
                    : <span className={`insignia ${c.estatus === 'Cancelado' ? 'aviso' : 'vigente'}`}>{c.estatus}</span>}
                </td>
                <td>
                  <span className={`insignia ${conciliacion.grado}`}>{conciliacion.texto}</span>
                </td>
                <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                  {/* El folio va en el chip de al lado: es lo que se busca al llegar a Pólizas. */}
                  {c.polizaId ? (
                    <Link className="enlace" to={`/clientes/${clienteId}/polizas`}>Revisar póliza</Link>
                  ) : enRiesgo ? (
                    <Link className="enlace" to="/clientes">Revisar riesgo</Link>
                  ) : c.estadoContabilizacion === 'Pendiente' ? (
                    <Link className="enlace" to={`/clientes/${clienteId}/contabilizacion`}>Contabilizar</Link>
                  ) : null}
                </td>
              </tr>
              )
            })}
          </tbody>
        </table>
        </div>
      )}

      {pagina.items.length > 0 && (
        <p className="menuda" style={{ marginTop: 'var(--space-3)' }}>
          Mostrando {pagina.items.length} de {pagina.total} con este filtro{' '}
          <button className="enlace" disabled={numeroDePagina <= 1}
            onClick={() => setNumeroDePagina((n) => Math.max(1, n - 1))}>← anterior</button>{' '}
          <span className="mono">página {pagina.pagina}</span>{' '}
          <button className="enlace" disabled={numeroDePagina * TAMANO_DE_PAGINA >= pagina.total}
            onClick={() => setNumeroDePagina((n) => n + 1)}>siguiente →</button>
        </p>
      )}

      {descargando && (
        <SolicitudDeDescargaMasiva
          clienteId={clienteId}
          alCerrar={(mensaje) => {
            setDescargando(false)
            if (mensaje) avisar(mensaje)
            void recargarSolicitudes()
          }}
        />
      )}

      {importando && (
        <ImportacionDeCfdi
          clienteId={clienteId}
          alCerrar={(mensaje) => {
            setImportando(false)
            if (mensaje) avisar(mensaje)
            void recargar()
          }}
        />
      )}

      {abierto && <ModalDeCfdi clienteId={clienteId} cfdi={abierto} alCerrar={() => setAbierto(null)} />}
    </>
  )
}

/** Solicitud de descarga masiva ante el SAT: autentica con la e.firma del cliente (RF-501). */
function SolicitudDeDescargaMasiva({
  clienteId,
  alCerrar,
}: {
  clienteId: string
  alCerrar: (aviso?: string) => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, alCerrar)
  const [tipo, setTipo] = useState<TipoDescargaCfdi>('Recibidos')
  const [fechaInicial, setFechaInicial] = useState('')
  const [fechaFinal, setFechaFinal] = useState('')
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  async function solicitar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    setError('')
    setEnviando(true)
    try {
      const lote = await api.solicitarDescargaMasiva(clienteId, { tipo, fechaInicial, fechaFinal })
      const partes = lote.particiones.length

      if (lote.errores.length === 0) {
        alCerrar(
          partes > 1
            ? `Rango dividido en ${partes} solicitudes (máx. 90 días c/u); todas registradas. Se presentan al SAT y se verifican solas en segundo plano.`
            : 'Solicitud registrada. Se presenta al SAT y se verifica sola en segundo plano.',
        )
      } else {
        alCerrar(
          `${partes} de ${partes + lote.errores.length} tramos registrados; ` +
            `${lote.errores.length} fallaron: ${lote.errores.map((e) => `${e.fechaInicial} a ${e.fechaFinal} (${e.mensaje})`).join('; ')}`,
        )
      }
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo solicitar la descarga.')
      setEnviando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={solicitar}
      >
        <h2 id={idTitulo}>Descargar por rango de fechas</h2>
        <p className="sutil">
          Se registra aquí y, en segundo plano, se autentica ante el SAT con la e.firma vigente del
          cliente siguiendo el flujo oficial: solicitud → verificación → descarga de paquetes
          (RF-501, RF-502). Máximo 90 días por solicitud.
        </p>

        <label>
          Comprobantes
          <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoDescargaCfdi)}>
            <option value="Recibidos">Recibidos</option>
            <option value="Emitidos">Emitidos</option>
          </select>
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          <label>
            Del
            <input type="date" value={fechaInicial} onChange={(e) => setFechaInicial(e.target.value)} required />
          </label>
          <label>
            Al
            <input type="date" value={fechaFinal} onChange={(e) => setFechaFinal(e.target.value)} required />
          </label>
        </div>

        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button type="button" onClick={() => alCerrar()}>Cancelar</button>
          <button type="submit" className="primario" disabled={enviando}>
            {enviando ? 'Solicitando…' : 'Solicitar descarga'}
          </button>
        </div>
      </form>
    </div>
  )
}

/**
 * Importación manual con reporte por archivo (RF-503, D-03). Desde P-95 el servidor solo encola
 * (RNF-02) y aquí se sondea el avance hasta que termina (RNF-04: "proceso asíncrono con avance visible").
 */
function ImportacionDeCfdi({
  clienteId,
  alCerrar,
}: {
  clienteId: string
  alCerrar: (aviso?: string) => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, alCerrar)
  const [error, setError] = useState('')
  const [resultado, setResultado] = useState<AvanceDeImportacion | null>(null)
  const [avance, setAvance] = useState<AvanceDeImportacion | null>(null)
  const [enviando, setEnviando] = useState(false)

  async function importar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const entrada = evento.currentTarget.elements.namedItem('archivos') as HTMLInputElement
    if (!entrada.files?.length) return
    setError('')
    setEnviando(true)
    try {
      let r = await api.importarCfdi(clienteId, entrada.files)
      setAvance(r)
      while (r.estado === 'Encolada' || r.estado === 'EnProceso') {
        await esperar(configuracionDeSondeo.importacionMs)
        r = await api.obtenerImportacionCfdi(clienteId, r.id)
        setAvance(r)
      }
      if (r.estado === 'Error') {
        setError(r.mensaje ?? 'La importación falló.')
      } else if (r.errores.length === 0) {
        alCerrar(`${r.importados} CFDI importados${r.duplicados ? `, ${r.duplicados} duplicados omitidos` : ''}.`)
      } else {
        setResultado(r)
      }
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudieron importar los archivos.')
    } finally {
      setEnviando(false)
      setAvance(null)
    }
  }

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={importar}
      >
        <h2 id={idTitulo}>Importar CFDI</h2>
        <p className="sutil">
          Acepta .xml timbrados y .zip con XML adentro. Los UUID ya presentes se omiten
          (deduplicación) y el XML original se conserva byte a byte (RN-04).
        </p>

        <label>
          Archivos XML o ZIP
          <input name="archivos" type="file" accept=".xml,.zip" multiple required />
        </label>

        {error && <p className="error" role="alert">{error}</p>}

        {avance && (
          <div className="aviso" role="status">
            <progress style={{ width: '100%' }} value={avance.total ? avance.procesados : undefined} max={avance.total || undefined} />
            <div className="sutil" style={{ margin: 0 }}>
              {avance.estado === 'Encolada' || avance.total === 0
                ? 'En cola: preparando los archivos…'
                : `${avance.procesados} de ${avance.total} procesados · ${avance.importados} importados · ${avance.duplicados} duplicados · ${avance.totalDeErrores} con error`}
            </div>
          </div>
        )}

        {resultado && (
          <div className="aviso" role="status" style={{ maxHeight: 220, overflow: 'auto' }}>
            <strong>
              {resultado.importados} importados · {resultado.duplicados} duplicados ·{' '}
              {resultado.totalDeErrores} con error:
            </strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {resultado.errores.map((e, i) => (
                <li key={i}><span className="mono">{e.archivo}</span>: {e.mensaje}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="acciones">
          <button type="button" onClick={() => alCerrar(resultado ? 'Importación parcial completada.' : undefined)}>
            {resultado ? 'Cerrar' : 'Cancelar'}
          </button>
          <button type="submit" className="primario" disabled={enviando}>
            {enviando ? 'Importando…' : 'Importar'}
          </button>
        </div>
      </form>
    </div>
  )
}

/**
 * Envoltorio de diálogo del visor de CFDI (RF-506, D-07): el contenido —las pestañas
 * Factura/Campos/XML— vive en `componentes/VisorDeCfdi`, compartido con la pantalla de
 * contabilización, que lo monta como columna y no como modal.
 */
function ModalDeCfdi({
  clienteId,
  cfdi,
  alCerrar,
}: {
  clienteId: string
  cfdi: CfdiDelRepositorio
  alCerrar: () => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLDivElement>(true, alCerrar)

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(680px, 94vw)' }}
      >
        <h2 id={idTitulo}>
          {cfdi.tipo} {cfdi.serie ?? ''}{cfdi.folio ? `-${cfdi.folio}` : ''} · {dinero(cfdi.total)} {cfdi.moneda}
        </h2>

        <VisorDeCfdi clienteId={clienteId} cfdi={cfdi} estatus={cfdi.estatus} usoCfdi={cfdi.usoCfdi} />

        <div className="acciones">
          <button className="primario" onClick={alCerrar}>Cerrar</button>
        </div>
      </div>
    </div>
  )
}
